/**
 * 워크스페이스 GNB 진행 레일 선언 — `WORKSPACE_NAV` 파생.
 *
 * 축명·설명·대표 route 는 전부 workspaceNav.ts 가 소유한다(075 이중 선언 통합 유지). 이 파일은 그
 * 배열을 **1차원 레일**(① 입력 → ② 분석 / 보관함은 레일 밖)로 옮기고, 축마다
 * "채워짐"을 판정할 재료 키를 매핑한다. 하위 화면이 2종 이상인 축(성적 분석 정시/수시)은
 * `items` 로 드롭다운 선택지를 함께 파생한다(`subItemsOf`). 새 축은 여기가 아니라 WORKSPACE_NAV 에 1행 추가하고
 * 아래 `RAIL_ORDER` 에 순번·재료만 지정한다.
 *
 * ## ⚠️ `landingAxes.ts` 의 `AXIS_FLOW` 와 왜 합치지 않았나
 *
 * 둘 다 WORKSPACE_NAV 파생이고 "깔때기"라는 같은 모델을 쓰지만, 소유하는 축이 다르다:
 *  - `AXIS_FLOW`(랜딩)  = **배치**(전폭/2열/틴트·step 배지·tone 색) — 2차원 지면 위 카드 레이아웃.
 *  - `RAIL_ORDER`(GNB) = **레일 탑승 여부**와 **재료 매핑**(순번은 노출 순으로 파생).
 *
 * 랜딩은 병렬 축이 **step 을 공유**할 수 있지만, 레일은 1차원이라 순번이 반드시 갈려야 한다. 한 Record 로
 * 합치면 랜딩 배치를 바꿀 때 GNB 순번이 함께 흔들리고, 그 역도 마찬가지다. 같은 데이터에서 파생되는 두 개의
 * 다른 관심사로 둔다.
 *
 * ## 채워짐 판정은 `Readiness.items[]` 를 접는다
 *
 * 평면 필드(`suneungEntered`·`naesinEntered` 등)가 아니라 `items[]`(재료 8항목) 를 쓴다 — `items[]`
 * 가 076 에서 도입된 정규 표현이다. 평면 필드는 045 시대 잔존물이라 새 소비처를 늘리지 않는다.
 * 판정 자체는 `useWorkspaceProgress` 가 수행하고, 이 파일은 "어떤 재료가 그 축에 속하는가"라는 선언과
 * 축별 상태 카피(`RAIL_COPY`)만 소유한다.
 */
import { ARCHIVE_KINDS, REPORT_REGISTRY, reportArchiveRoute } from './reportRegistry';
import {
  VISIBLE_WORKSPACE_NAV,
  WORKSPACE_NAV,
  type WorkspaceNavGroup,
  type WorkspaceNavKey,
  type WorkspaceSubItem,
} from './workspaceNav';

import type { MaterialKey } from '#/api/analysis/readiness';

/** 레일 순번 — 노출 축 순서로 1부터 매긴다(숨김·삭제 시 공백 없이 재산출). 보관함은 레일 밖 별도 슬롯. */
export type RailSeq = number;

/**
 * 축 상태 — 점의 의미.
 *
 * 🔴 `empty` 는 **잠금이 아니다**. 043 SC-007(해금·완주 조건을 암시하는 카피·시각 요소 0건)·045
 *    FR-020(병렬 재료)이 순차 잠금 시각을 금지하고, 실제로도 잠긴 축은 0개다 — `canGenerate` 는
 *    성적·진단·검사 중 1종만 있으면 true 이고 `/tests` 는 선행 조건이 없다. `empty` 축도 클릭해
 *    바로 들어갈 수 있으며, 표시는 평상 톤을 유지한다(opacity 감쇠·disabled 금지 — WorkspaceRailDot 참조).
 *
 *  - `filled` : 그 축에 사용자가 남긴 재료·결과가 있다.
 *  - `next`   : 아직 비어 있는 **첫** 축 = 재개 지점. 정확히 하나만 존재한다.
 *  - `empty`  : 아직 비어 있다(클릭 가능).
 *  - `unknown`: 비로그인·로딩·조회 실패 — 점을 그리지 않는다.
 */
export type RailAxisState = 'filled' | 'next' | 'empty' | 'unknown';

/**
 * 축 key → 레일 탑승 여부 + 채워짐 판정 재료. 새 축 추가 시 여기에 1행. 순번은 선언하지 않는다 —
 * 노출 축(`VISIBLE_WORKSPACE_NAV`) 순서로 조립 시 매긴다(숨긴 축이 있어도 ①②③ 이 이어진다).
 *
 * `fills` 는 **OR 판정**이다 — 하나라도 FILLED 면 그 축은 채워진 것으로 본다(045 FR-020 "한쪽이라도
 * 저장 시 입력됨"). 예: 내 입시카드는 수능·내신 중 하나만 넣어도 채워짐. 전부 요구하면 완주 서사가 된다.
 */
const RAIL_ORDER: Record<WorkspaceNavKey, { onRail: boolean; fills: MaterialKey[] }> = {
  card: { onRail: true, fills: ['SUNEUNG', 'NAESIN'] },
  score: { onRail: true, fills: ['DIAG_JEONGSI', 'DIAG_SUSI'] },
  test: {
    onRail: true,
    fills: ['TEST_CAREER', 'TEST_LEARNING', 'TEST_PSYCH', 'TEST_ADMISSION'],
  },
  // 보관함은 "채운다"는 개념이 없다(결과가 쌓이는 저장소) — 선행 조건도 없다.
  // landingAxes 의 `flow: 'archive'`(step·tone 둘 다 없음)와 같은 판단.
  reports: { onRail: false, fills: [] },
};

/**
 * 레일 축별 상태 카피 — 점의 툴팁·모바일 병기 문구. `emptyHint` 는 "비어 있으면 무엇을 못 보는가"만 말한다.
 *
 * ⚠️ 해금·완주·잠금 같은 선행조건 어휘를 넣지 말 것 — 실제로 잠긴 축은 0개이므로 사실도 아니다(043 SC-007).
 */
export const RAIL_COPY: Record<WorkspaceNavKey, { emptyHint: string; filledHint: string }> = {
  card: {
    emptyHint: '성적이 없으면 정시·수시 판정이 자료 부족으로 남아요',
    filledHint: '성적이 저장돼 있어요',
  },
  score: {
    emptyHint: '분석 결과를 저장하면 보관함에서 다시 볼 수 있어요',
    filledHint: '분석 결과가 있어요',
  },
  test: {
    emptyHint: '검사 결과가 없어요',
    filledHint: '검사 결과가 있어요',
  },
  reports: {
    emptyHint: '',
    filledHint: '',
  },
} as const;

/** 드롭다운 1항목 — 축 하위 화면(정시/수시) 또는 보관함 `?kind=` 필터 딥링크. */
export interface RailSubItem {
  label: string;
  route: string;
  desc: string;
}

/** 레일 한 칸. */
export interface RailAxis {
  key: WorkspaceNavKey;
  /** 표시 라벨 — WORKSPACE_NAV 소유(breadcrumb 문자열 매칭이 의존하므로 여기서 바꾸지 않는다). */
  label: string;
  /** 대표 진입 route. */
  route: string;
  /** 레일 순번(보관함은 레일 밖이라 미보유). */
  seq: RailSeq;
  /** 채워짐 판정 재료(OR). */
  fills: MaterialKey[];
  /**
   * 드롭다운 하위 항목 — **서로 다른 화면**으로 가는 활성 항목이 2개 이상일 때만 채운다(아니면 빈 배열 =
   * 직접 링크). 같은 화면의 앵커(`/card#basic`…)는 선택지가 아니라 한 화면이므로 펼치지 않는다.
   *
   * 이 규칙을 데이터에서 계산하는 이유: 레일 전환 커밋이 "4축은 하위 항목이 1개거나 같은 화면 앵커"라고
   * **가정**하고 전부 직접 링크로 폈는데, 성적 분석(정시 `/jeongsi`·수시 `/susi`)은 그 가정이 틀려
   * 수시 진입점이 GNB 에서 사라졌다. 가정을 코드로 옮겨 두면 축에 화면이 추가돼도 자동으로 펼쳐진다.
   */
  items: RailSubItem[];
}

/** 축 하위 항목 → 드롭다운 항목. 화면(pathname)이 2종 이상일 때만 선택지로 인정한다. */
function subItemsOf(group: WorkspaceNavGroup): RailSubItem[] {
  const active = group.items.filter(
    (item): item is WorkspaceSubItem & { route: string } =>
      item.status === 'active' && item.route !== undefined,
  );
  const screens = new Set(
    active.map((item) => new URL(item.route, 'https://workspace.local').pathname),
  );
  if (screens.size < 2) return [];
  return active.map((item) => ({ label: item.label, route: item.route, desc: item.desc ?? '' }));
}

/**
 * 레일 축(노출 순) — 보관함·숨긴 축 제외. 순번은 배열 위치로 매긴다.
 * `RAIL_ORDER` 미선언 축은 레일에 싣지 않는다(선언을 빠뜨린 신규 축이 순번 없이 끼어들지 않게).
 */
export const RAIL_AXES: RailAxis[] = VISIBLE_WORKSPACE_NAV.filter(
  (group) => RAIL_ORDER[group.key].onRail,
).map((group, index) => ({
  key: group.key,
  label: group.label,
  route: group.route,
  seq: index + 1,
  fills: RAIL_ORDER[group.key].fills,
  items: subItemsOf(group),
}));

/**
 * 보관함 — 레일 밖 우측 슬롯(구분선 뒤). 점을 그리지 않고 드롭다운만 가진다.
 * 항목 라벨·설명은 레지스트리 SSoT 에서 읽는다(재기입 금지 — 072 표시 종류 4종).
 */
export const RAIL_ARCHIVE: { label: string; route: string; items: RailSubItem[] } = {
  label: WORKSPACE_NAV.find((g) => g.key === 'reports')?.label ?? '리포트 보관함',
  route: reportArchiveRoute(),
  items: ARCHIVE_KINDS.map((kind) => ({
    label: REPORT_REGISTRY[kind].label,
    route: reportArchiveRoute(kind),
    desc: REPORT_REGISTRY[kind].desc,
  })),
};

/**
 * 축 활성 판정 — 드롭다운 축은 하위 항목 중 하나라도 현재 위치면 활성(정시·수시 어느 쪽에 있어도
 * "성적 분석"이 켜진다), 직접 링크 축은 대표 route 기준. 데스크톱 레일·모바일 세로 레일이 공유한다.
 */
export function isRailAxisActive(
  axis: Pick<RailAxis, 'route' | 'items'>,
  currentPath: string,
  currentKind: string | undefined,
): boolean {
  if (axis.items.length > 0) {
    return axis.items.some((item) => isRailItemActive(item.route, currentPath, currentKind));
  }
  return isRailItemActive(axis.route, currentPath, currentKind);
}

/**
 * 항목 route 와 현재 라우트의 일치 판정 — WorkspaceShell 에서 이관(로직 무변경).
 *
 * pathname 정확 일치·하위 경로 접두 일치에 더해, 항목이 `?kind=` 쿼리를 가지면 그 값까지 일치해야
 * 한다(065 보관함 4항목이 각각 활성 표시). 항목 route 는 `#앵커`·`?쿼리` 를 품을 수 있어 `URL` 로
 * 분해한다 — 문자열 비교는 절대 일치하지 않는다.
 *
 * ⚠️ `kind === null` 이면 쿼리를 보지 않으므로, 이 함수로 "전체"(`/reports`, kind 없음) 행을 판정하면
 *    `?kind=OPINION` 에서도 활성으로 뜬다. 드롭다운에 "전체" 행을 넣으려면 정확 일치 분기를 따로 둘 것.
 */
export function isRailItemActive(
  itemRoute: string,
  currentPath: string,
  currentKind: string | undefined,
): boolean {
  const target = new URL(itemRoute, 'https://workspace.local');
  const pathMatches =
    currentPath === target.pathname || currentPath.startsWith(`${target.pathname}/`);
  if (!pathMatches) return false;
  const kind = target.searchParams.get('kind');
  return kind === null || currentKind === kind;
}
